<template>
  <v-snackbar
    v-model="needRefresh"
    :timeout="-1"
    location="bottom"
    color="primary"
    multi-line
  >
    <div class="d-flex align-center">
      <v-icon class="mr-3">mdi-update</v-icon>
      <div>
        <div class="font-weight-medium">{{ t('pwa.updateAvailable') }}</div>
        <div class="text-caption">{{ t('pwa.updateDescription') }}</div>
      </div>
    </div>

    <template #actions>
      <v-btn variant="text" @click="close">
        {{ t('pwa.later') }}
      </v-btn>
      <v-btn variant="flat" color="white" @click="handleUpdate">
        {{ t('pwa.update') }}
      </v-btn>
    </template>
  </v-snackbar>

  <!-- Offline indicator -->
  <v-snackbar
    v-model="offlineReady"
    :timeout="3000"
    location="bottom"
    color="success"
  >
    <v-icon class="mr-2">mdi-check-circle</v-icon>
    {{ t('pwa.offlineReady') }}
  </v-snackbar>
</template>

<script setup lang="ts">
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const { offlineReady, needRefresh, updateServiceWorker } = useRegisterSW({
  onRegistered(r) {
    // Проверяем обновления каждый час
    r &&
      setInterval(
        () => {
          r.update()
        },
        60 * 60 * 1000
      )
  },
  onRegisterError(error) {
    console.error('SW registration error:', error)
  },
})

/**
 * Очистка всех кэшей MF модулей при обновлении приложения.
 * Удаляет:
 * - CacheStorage: все кэши workbox и mf-*
 * - localStorage: кэши манифестов и конфигов модулей
 * Сохраняется: авторизация, настройки пользователя
 */
async function clearMFCaches(): Promise<void> {
  // 1. Очистка CacheStorage (Service Worker caches)
  if ('caches' in window) {
    const cacheNames = await caches.keys()
    // Удаляем ВСЕ кэши workbox — они будут пересозданы с новой версией
    const cachesToDelete = cacheNames.filter(
      (name) =>
        name.includes('mf-') || // mf-manifests, mf-entries, mf-configs
        name.includes('yc-mf-') || // yc-mf-manifests, yc-mf-entries, yc-mf-configs
        name.includes('shell-') || // shell-html, shell-config
        name.includes('workbox-') // Все workbox кэши (precache, runtime)
    )

    await Promise.all(cachesToDelete.map((name) => caches.delete(name)))
    console.log('[PWA] Cleared CacheStorage caches:', cachesToDelete)
  }

  // 2. Очистка localStorage для MF модулей
  // Удаляем кэши манифестов и конфигов, но сохраняем авторизацию и настройки
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (
      key &&
      (key.startsWith('mf-') || // mf-manifests, mf-config-*
        key.startsWith('tlh-mf-') || // tlh-mf-* кэши
        key.startsWith('module-config-') || // module-config-* кэши конфигов
        key.startsWith('org-modules-')) // org-modules-* списки модулей
    ) {
      keysToRemove.push(key)
    }
  }

  for (const key of keysToRemove) {
    localStorage.removeItem(key)
  }

  console.log('[PWA] Cleared localStorage keys:', keysToRemove)
}

/**
 * Обновление приложения с очисткой кэша MF.
 */
async function handleUpdate(): Promise<void> {
  try {
    // 1. Очищаем кэши MF модулей
    await clearMFCaches()

    // 2. Обновляем Service Worker и перезагружаем страницу
    await updateServiceWorker(true) // true = reload

    // Если updateServiceWorker не перезагрузил страницу (например, SW не активен),
    // принудительно перезагружаем
    setTimeout(() => {
      console.log('[PWA] Force reload fallback')
      window.location.reload()
    }, 1000)
  } catch (error) {
    console.error('[PWA] Update failed:', error)
    // При ошибке всё равно перезагружаем страницу
    window.location.reload()
  }
}

function close() {
  offlineReady.value = false
  needRefresh.value = false
}
</script>
